import React, { FormEvent, useEffect, useState } from 'react';
import './DetailsPage.css';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Alert, Breadcrumbs, Button, Chip, CircularProgress, Divider, Rating, TextField, Typography } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import itemsServiceInstance from '../../common/Rest/Services/ItemService/ItemService';
import { BookResponseData, ReviewData } from '../../common/Rest/Services/ItemService/Data';
import reviewService from '../../common/Rest/Services/ReviewService/ReviewService';
import ROUTES from '../../common/Routing/Routes';
import { Item, ItemTypeEnum } from '../../common/Rest/Services/Data';
import { getBookImageSrcSet, getBookImageUrl } from '../../common/Utils/BookImages';
import { useCart } from '../../../contexts/CartContext';
import { useAuth } from '../../common/Auth/AuthProvider';
import wishListService from '../../common/Rest/Services/WishListService/WishListService';
import waitlistService, { WaitlistStatus } from '../../common/Rest/Services/WaitlistService/WaitlistService';
import BookCoverFrame from '../../common/BookCoverFrame/BookCoverFrame';
import { ApiError } from '../../common/Rest/ReaderlyHttpClient';
import { showErrorSnackbar } from '../../common/Utils/SnackbarService';

const formatPrice = (item: BookResponseData) => `${item.price} грн донату за оренду`;

const readerCountText = (count: number, isAvailable: boolean) => {
  if (count === 0) {
    return isAvailable
      ? 'Ви можете стати першим читачем, який орендує цю книгу.'
      : 'Ще ніхто не орендував цю книгу.';
  }
  const lastTwo = count % 100;
  const last = count % 10;
  const noun = lastTwo >= 11 && lastTwo <= 14
    ? 'читачів'
    : last === 1
      ? 'читач'
      : last >= 2 && last <= 4
        ? 'читачі'
        : 'читачів';
  const verb = last === 1 && lastTwo !== 11 ? 'орендував' : 'орендували';
  return `${count} ${noun} вже ${verb} цю книгу.`;
};

const readerCountShortText = (count: number) => {
  const lastTwo = count % 100;
  const last = count % 10;
  const noun = lastTwo >= 11 && lastTwo <= 14
    ? 'читачів'
    : last === 1
      ? 'читач'
      : last >= 2 && last <= 4
        ? 'читачі'
        : 'читачів';
  return `${count} ${noun}`;
};

const readerCountCaption = (count: number) => {
  if (count === 1) return 'Цей примірник уже побував у руках першого читача Readerly.';
  return 'Цей примірник уже побував у руках читачів Readerly.';
};

const reviewCountText = (count: number) => {
  if (count === 0) return 'Поки немає відгуків';
  const lastTwo = count % 100;
  const last = count % 10;
  const noun = lastTwo >= 11 && lastTwo <= 14
    ? 'відгуків'
    : last === 1
      ? 'відгук'
      : last >= 2 && last <= 4
        ? 'відгуки'
        : 'відгуків';
  return `${count} ${noun}`;
};

const formatReviewDate = (value?: string) => {
  if (!value) return 'дата не вказана';
  return new Date(value).toLocaleDateString('uk-UA', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  });
};

const formatLedgerDate = (value?: string) => {
  if (!value) return 'дата не вказана';
  return new Date(value).toLocaleDateString('uk-UA');
};

const readerName = (review: ReviewData) =>
  review.displayName || review.userName || 'Читач Readerly';

const hasDisplayIsbn = (isbn?: string) => {
  const value = isbn?.trim();
  return Boolean(value && value.toLowerCase() !== 'isbn' && /[0-9x]/i.test(value));
};

const availabilityText = (stockCount: number, readerCount: number) => {
  if (stockCount > 0) return `Доступно для оренди: ${stockCount} шт.`;
  if (readerCount > 0) {
    return 'Книгу зараз читають. Вона вже в чиїхось руках, а щойно повернеться на полицю, її знову можна буде орендувати.';
  }
  return 'Наразі книги немає в наявності.';
};

const toCartItem = (item: BookResponseData): Item => ({
  id: item.id,
  name: item.name,
  type: item.type as unknown as ItemTypeEnum,
  stockCount: item.stockCount ?? 0,
  price: item.price,
  priceType: item.priceType,
  count: 1,
  image: getBookImageUrl(item),
  description: item.description,
  isInWishList: item.isInWishList,
});

function DetailsPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addToCart, isInCart, openCartModal } = useCart();
  const { isAuthenticated } = useAuth();
  const [item, setItem] = useState<BookResponseData | null>(null);
  const [reviews, setReviews] = useState<ReviewData[]>([]);
  const [reviewText, setReviewText] = useState('');
  const [reviewRating, setReviewRating] = useState<number | null>(null);
  const [reviewError, setReviewError] = useState<string | null>(null);
  const [wishlisted, setWishlisted] = useState(false);
  const [wishlistSubmitting, setWishlistSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [submittingReview, setSubmittingReview] = useState(false);
  const [waitlistStatus, setWaitlistStatus] = useState<WaitlistStatus | null>(null);
  const [waitlistSubmitting, setWaitlistSubmitting] = useState(false);

  useEffect(() => {
    if (!id) {
      navigate(ROUTES.NOT_FOUND);
      return;
    }

    const parsedId = Number(id);
    if (Number.isNaN(parsedId) || parsedId <= 0) {
      navigate(ROUTES.NOT_FOUND);
      return;
    }

    (async () => {
      setLoading(true);
      const response = await itemsServiceInstance.GetItemById(parsedId);
      if (!response) {
        navigate(ROUTES.NOT_FOUND);
        return;
      }
      setItem(response);
      setReviews(response.reviews ?? []);
      setWishlisted(response.isInWishList ?? false);
      setLoading(false);
    })();
  }, [id, navigate]);

  useEffect(() => {
    if (!item || (item.stockCount ?? 0) > 0 || !isAuthenticated) {
      setWaitlistStatus(null);
      return;
    }

    let ignore = false;
    waitlistService.GetStatus(item.id)
      .then((status) => {
        if (!ignore) setWaitlistStatus(status);
      })
      .catch(() => {
        if (!ignore) setWaitlistStatus(null);
      });

    return () => {
      ignore = true;
    };
  }, [item, isAuthenticated]);

  const toggleWaitlist = async () => {
    if (!item || waitlistSubmitting) return;
    if (!isAuthenticated) {
      navigate(ROUTES.LOGIN);
      return;
    }

    setWaitlistSubmitting(true);
    try {
      if (waitlistStatus?.inQueue) {
        await waitlistService.Leave(item.id);
      } else {
        await waitlistService.Join(item.id);
      }
      const status = await waitlistService.GetStatus(item.id);
      setWaitlistStatus(status);
    } catch {
      // Failure feedback is already shown by the global HTTP error toast.
    } finally {
      setWaitlistSubmitting(false);
    }
  };

  const toggleWishlist = async () => {
    if (!item || wishlistSubmitting) return;
    if (!isAuthenticated) {
      navigate(ROUTES.LOGIN);
      return;
    }

    setWishlistSubmitting(true);
    try {
      if (wishlisted) {
        await wishListService.DeleteFromWishList(item.id);
        setWishlisted(false);
      } else {
        await wishListService.AddToWishlist(item.id);
        setWishlisted(true);
      }
    } catch (error) {
      // The server treats add/remove as already-in-sync (400) when our local state was
      // stale — reconcile silently instead of showing a confusing error for a non-issue.
      if (error instanceof ApiError && error.status === 400) {
        setWishlisted(!wishlisted);
      } else {
        showErrorSnackbar('Не вдалося оновити вішліст. Спробуйте ще раз.');
      }
    } finally {
      setWishlistSubmitting(false);
    }
  };

  const submitReview = async (event: FormEvent) => {
    event.preventDefault();
    if (!item) return;
    if (!reviewText.trim()) {
      setReviewError('Напишіть кілька слів про книжку.');
      return;
    }

    try {
      setSubmittingReview(true);
      setReviewError(null);
      const created = await reviewService.create({ itemId: item.id, rating: reviewRating, text: reviewText.trim() });
      setReviews((previous) => [created, ...previous]);
      setReviewText('');
      setReviewRating(null);
    } catch {
      setReviewError('Не вдалося додати відгук. Спробуйте ще раз.');
    } finally {
      setSubmittingReview(false);
    }
  };

  if (loading) {
    return (
      <div className="details-page default-container details-loading">
        <CircularProgress />
      </div>
    );
  }

  if (!item) return null;

  const authors = item.authors ?? [];
  const genres = item.genres ?? [];
  const tags = item.tags ?? [];
  const descriptionParagraphs = item.description?.split(/\n+/).filter((text) => text.trim()) ?? [];
  const inCart = isInCart(item.id);
  const isAvailable = (item.stockCount ?? 0) > 0;
  const readerCount = item.readerCount ?? 0;
  const formReaders = reviews.slice(0, 10);

  return (
    <div className="details-page default-container">
      <div className="details-breadcrumbs">
        <Link to={ROUTES.HOME} className="details-back-link">
          <ArrowBackIcon fontSize="small" />
          Назад до каталогу
        </Link>
        <Breadcrumbs aria-label="breadcrumb">
          <Link to={ROUTES.HOME}>Книги</Link>
          <Typography color="text.primary">{item.name}</Typography>
        </Breadcrumbs>
      </div>

      <section className="details-hero">
        <div className="details-cover-wrap">
          <BookCoverFrame
            src={getBookImageUrl(item, 'medium')}
            srcSet={getBookImageSrcSet(item)}
            sizes="(max-width: 960px) calc(100vw - 48px), 420px"
            alt={item.name}
            className="details-cover"
            imageClassName="details-cover-image"
          />
        </div>

        <div className="details-main">
          <div>
            <Typography variant="overline">Оренда книг</Typography>
            <Typography variant="h3" component="h1">
              {item.name}
            </Typography>
          </div>

          {authors.length > 0 && (
            <div className="chip-row">
              {authors.map((author) => (
                <Chip
                  key={author.id}
                  label={author.name}
                  component={Link}
                  to={`${ROUTES.HOME}?author=${author.id}`}
                  clickable
                />
              ))}
            </div>
          )}

          {(item.rating != null || item.internalRating != null) && (
            <div className="rating-row">
              {item.rating != null && (
                <span>
                  Зовнішній рейтинг {item.rating}
                  <Rating value={item.rating} readOnly precision={0.1} />
                </span>
              )}
              {item.internalRating != null && (
                <span>
                  Рейтинг Readerly {item.internalRating}
                  <Rating value={item.internalRating} readOnly precision={0.1} />
                </span>
              )}
            </div>
          )}

          <div>
            <Typography className="details-price">{formatPrice(item)}</Typography>
            <Typography variant="body2" color="text.secondary">
              Платите один раз, без жорсткого терміну. Усі гроші йдуть на дрони для ЗСУ.{' '}
              <Link to={ROUTES.FAQ}>Як це працює</Link>
            </Typography>
          </div>

          <div className="reader-slip">
            {readerCountText(readerCount, isAvailable)}
          </div>

          <div className="details-facts">
            {hasDisplayIsbn(item.isbn) && <span>ISBN: {item.isbn}</span>}
            {item.pages && <span>{item.pages} стор.</span>}
            {item.publisher?.name && (
              <Link to={`${ROUTES.HOME}?publisher=${item.publisher.id}`}>Видавництво: {item.publisher.name}</Link>
            )}
            {item.series && (
              <Link to={`${ROUTES.HOME}?series=${item.series.id}&seriesName=${encodeURIComponent(item.series.name)}`}>
                Серія: {item.series.name}
                {item.seriesNumber != null ? ` • №${item.seriesNumber}` : ''}
              </Link>
            )}
          </div>

          {genres.length > 0 && (
            <div className="chip-row">
              {genres.map((genre) => (
                <Chip
                  key={genre.id}
                  label={genre.name}
                  variant="outlined"
                  component={Link}
                  to={`${ROUTES.HOME}?genre=${genre.id}`}
                  clickable
                />
              ))}
            </div>
          )}

          {tags.length > 0 && (
            <div className="chip-row">
              {tags.map((tag) => (
                <Chip
                  key={tag.id}
                  label={`#${tag.name}`}
                  size="small"
                  clickable
                  component={Link}
                  to={`${ROUTES.HOME}?tag=${tag.id}`}
                />
              ))}
            </div>
          )}

          <Alert severity={isAvailable ? 'success' : 'warning'}>
            {availabilityText(item.stockCount ?? 0, readerCount)}
          </Alert>

          <div className="details-actions">
            <Button
              variant="contained"
              startIcon={<MenuBookIcon />}
              disabled={!isAvailable && !inCart}
              onClick={() => {
                if (inCart) {
                  openCartModal();
                } else {
                  addToCart(toCartItem(item));
                }
              }}
            >
              {inCart ? 'До кошика' : 'Орендувати'}
            </Button>
            {!isAvailable && (
              <Button
                variant="outlined"
                startIcon={waitlistStatus?.inQueue ? <NotificationsActiveIcon /> : <NotificationsNoneIcon />}
                onClick={toggleWaitlist}
                disabled={waitlistSubmitting}
              >
                {waitlistStatus?.inQueue
                  ? `У черзі (${waitlistStatus.position ?? '?'})`
                  : 'Повідомити, коли звільниться'}
              </Button>
            )}
            <Button variant="outlined" startIcon={<FavoriteBorderIcon />} onClick={toggleWishlist} disabled={wishlistSubmitting}>
              {wishlisted ? 'У вішлісті' : 'До вішліста'}
            </Button>
          </div>
        </div>
      </section>

      <section className="details-content">
        <article className="details-panel">
          <Typography variant="h5">Опис</Typography>
          {descriptionParagraphs.length ? (
            descriptionParagraphs.map((paragraph, index) => <p key={index}>{paragraph}</p>)
          ) : (
            <p className="muted">Опис скоро з'явиться.</p>
          )}
        </article>

        <aside className="details-panel">
          {readerCount > 0 && (
            <div className="library-form">
              <div className="library-form-top">
                <span>Формуляр книги</span>
                <strong>{readerCountShortText(readerCount)}</strong>
                <p>{readerCountCaption(readerCount)} Нижче останні записи з читацької історії.</p>
              </div>
              {formReaders.length > 0 ? (
                <div className="library-form-readers">
                  {formReaders.map((review) => (
                    <div key={review.id} className="library-form-reader">
                      <span>{readerName(review)} читала {formatLedgerDate(review.createdAt)}</span>
                      <b>{review.rating ? `рейтинг ${review.rating}/5` : 'без оцінки'}</b>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="library-form-note">
                  Відгуків ще немає, але книга вже має історію оренд. Наступний запис може бути вашим.
                </p>
              )}
            </div>
          )}
          {item.isLimitedEdition && <Chip label="Лімітоване видання" color="primary" />}
          <Typography variant="h6">Як орендувати</Typography>
          <Divider />
          <ol className="details-how-it-works">
            <li>Додаєте книжку в кошик і оформлюєте оренду.</li>
            <li>Ми пишемо вам з даними для донату й відправляємо книжку Укрпоштою чи Новою поштою.</li>
            <li>Читаєте без жорсткого терміну.</li>
            <li>Повертаєте — зворотну доставку оплачуємо ми.</li>
          </ol>
          <Link to={ROUTES.FAQ}>Питання й відповіді</Link>
        </aside>
      </section>

      <section className="reviews-section">
        <div className="reviews-header">
          <Typography variant="h5">Відгуки</Typography>
          <Typography color="text.secondary">{reviewCountText(reviews.length)}</Typography>
        </div>

        {isAuthenticated ? (
          <form className="review-form" onSubmit={submitReview}>
            <div className="review-rating-field">
              <span>Оцінка</span>
              <Rating
                value={reviewRating}
                onChange={(_, value) => setReviewRating(value)}
              />
            </div>
            <TextField
              label="Ваш відгук"
              value={reviewText}
              onChange={(event) => setReviewText(event.target.value)}
              multiline
              minRows={3}
              fullWidth
            />
            {reviewError && <Alert severity="error">{reviewError}</Alert>}
            <Button type="submit" variant="contained" disabled={submittingReview}>
              {submittingReview ? 'Публікуємо...' : 'Написати відгук'}
            </Button>
          </form>
        ) : (
          <Alert severity="info">
            <Link to={ROUTES.LOGIN}>Увійдіть</Link>, щоб написати відгук.
          </Alert>
        )}

        <div className="reviews-list">
          {reviews.map((review) => (
            <article key={review.id} className="review-card">
              <header className="review-card-header">
                <strong>{review.displayName || review.userName || 'Читач'}</strong>
                <span>{formatReviewDate(review.createdAt)}</span>
                <span>{review.rating ? `${review.rating}/5` : 'без оцінки'}</span>
              </header>
              {review.rating && <Rating value={review.rating} readOnly size="small" />}
              <p>{review.text}</p>
            </article>
          ))}
        </div>
      </section>
    </div>
  );
}

export default DetailsPage;
